import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { type FizboListing } from '@/hooks/useFizbo';
import SatilabilirlikSkoru from './SatilabilirlikSkoru';
import RakipKarsilastirma from './RakipKarsilastirma';
import PdfSunum from './PdfSunum';
import IlanYasamDongusu from './IlanYasamDongusu';
import HaritaLokasyon from './HaritaLokasyon';
import MusteriIlgisi from './MusteriIlgisi';
import DavranisAnalizi from './DavranisAnalizi';

interface FizboDetailModalProps {
  listing: FizboListing;
  onClose: () => void;
}

type TabId = 'detay' | 'skor' | 'rakip' | 'yasam' | 'harita' | 'musteri' | 'davranis' | 'pdf';

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  Kabul: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', dot: 'bg-green-500' },
  Düşünecek: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500' },
  Red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  'Başka Emlakçıda': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-400', dot: 'bg-gray-500' },
};

const ageBadge = (days: number) => {
  if (days <= 15) return { label: '0-15 Gün', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
  if (days <= 45) return { label: '15-45 Gün', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' };
  return { label: '45+ Gün', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
};

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: 'detay',    label: 'Detay',    icon: 'ri-file-list-3-line' },
  { id: 'skor',     label: 'Skor',     icon: 'ri-bar-chart-line' },
  { id: 'rakip',    label: 'Rakip',    icon: 'ri-building-2-line' },
  { id: 'yasam',    label: 'Yaşam',    icon: 'ri-history-line' },
  { id: 'harita',   label: 'Harita',   icon: 'ri-map-pin-line' },
  { id: 'musteri',  label: 'Müşteri',  icon: 'ri-user-heart-line' },
  { id: 'davranis', label: 'Analiz',   icon: 'ri-eye-line' },
  { id: 'pdf',      label: 'PDF',      icon: 'ri-file-pdf-line' },
];

export default function FizboDetailModal({ listing, onClose }: FizboDetailModalProps) {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<TabId>('detay');
  const statusStyle = statusColors[listing.status] || statusColors['Başka Emlakçıda'];
  const badge = ageBadge(listing.daysOld);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative w-full max-w-xl rounded-2xl shadow-glass max-h-[92vh] flex flex-col ${
          isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 pt-5 pb-4 border-b ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-home-line text-white text-lg" />
            </div>
            <div>
              <h2 className={`text-base font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                {listing.ownerName}
              </h2>
              <p className={`text-xs mt-0.5 font-mono ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                {listing.listingNo}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {listing.sellabilityScore && (
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${
                listing.sellabilityScore >= 80
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : listing.sellabilityScore >= 60
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                <i className="ri-bar-chart-line text-xs" />
                {listing.sellabilityScore}
              </div>
            )}
            <button
              onClick={onClose}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
              }`}
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Tabs - scrollable */}
        <div className={`border-b ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className="flex gap-0.5 px-3 pt-2 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2.5 py-2 text-xs font-medium rounded-t-lg transition-all cursor-pointer border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-500'
                    : `border-transparent ${isDark ? 'text-dark-muted hover:text-dark-text' : 'text-light-muted hover:text-light-text'}`
                }`}
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`${tab.icon} text-xs`} />
                </div>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* DETAY */}
          {activeTab === 'detay' && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1.5 ${statusStyle.bg} ${statusStyle.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                  {listing.status}
                </span>
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${badge.color}`}>{badge.label}</span>
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${isDark ? 'bg-dark-border text-dark-muted' : 'bg-light-border text-light-muted'}`}>
                  {listing.propertyType}
                </span>
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${isDark ? 'bg-dark-border text-dark-muted' : 'bg-light-border text-light-muted'}`}>
                  {listing.transactionType}
                </span>
              </div>

              <div className={`grid grid-cols-2 gap-3 p-4 rounded-xl ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
                {[
                  { label: 'Telefon', value: listing.phone },
                  { label: 'Lokasyon', value: listing.location },
                  { label: 'İlan Yaşı', value: `${listing.daysOld} Gün` },
                  { label: 'Eklenme Tarihi', value: listing.createdAt },
                  ...(listing.price ? [{ label: 'Fiyat', value: `${listing.price.toLocaleString('tr-TR')} ₺` }] : []),
                  ...(listing.sqm ? [{ label: 'Alan', value: `${listing.sqm} m²` }] : []),
                ].map((item) => (
                  <div key={item.label}>
                    <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{item.label}</p>
                    <p className={`text-sm font-medium mt-0.5 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{item.value}</p>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Yorgunluk Skoru</p>
                  <span className={`text-sm font-bold ${listing.fatigueScore < 30 ? 'text-green-500' : listing.fatigueScore < 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                    %{listing.fatigueScore}
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                  <div
                    className={`h-full rounded-full transition-all ${listing.fatigueScore < 30 ? 'bg-green-500' : listing.fatigueScore < 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${listing.fatigueScore}%` }}
                  />
                </div>
              </div>

              <div>
                <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Fiyat Geçmişi</h3>
                <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
                  {listing.priceHistory.map((item, idx) => (
                    <div key={idx} className={`flex items-center justify-between px-4 py-2.5 text-sm ${idx > 0 ? (isDark ? 'border-t border-dark-border' : 'border-t border-light-border') : ''}`}>
                      <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>{item.date}</span>
                      <span className={`font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{item.price.toLocaleString('tr-TR')} ₺</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Notlar</h3>
                <p className={`text-sm p-3 rounded-lg ${isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'}`}>
                  {listing.notes || 'Not eklenmemiş.'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'skor' && (
            <SatilabilirlikSkoru score={listing.sellabilityScore ?? 50} listingNo={listing.listingNo} />
          )}

          {activeTab === 'rakip' && (
            <RakipKarsilastirma myPrice={listing.price ?? 0} mySqm={listing.sqm ?? 100} myLocation={listing.location} />
          )}

          {activeTab === 'yasam' && (
            <IlanYasamDongusu daysOld={listing.daysOld} createdAt={listing.createdAt} status={listing.status} />
          )}

          {activeTab === 'harita' && (
            <HaritaLokasyon location={listing.location} />
          )}

          {activeTab === 'musteri' && (
            <MusteriIlgisi
              propertyType={listing.propertyType}
              transactionType={listing.transactionType}
              price={listing.price}
              sqm={listing.sqm}
            />
          )}

          {activeTab === 'davranis' && (
            <DavranisAnalizi />
          )}

          {activeTab === 'pdf' && (
            <PdfSunum listing={listing} />
          )}
        </div>

        {/* Footer Actions */}
        <div className={`flex gap-2 px-5 py-4 border-t ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <a
            href={`tel:${listing.phone.replace(/\s/g, '')}`}
            className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line" />
            </div>
            Ara
          </a>
          <button className={`flex-1 py-2.5 font-medium text-sm rounded-lg border transition-colors cursor-pointer ${
            isDark ? 'border-dark-border text-dark-text hover:bg-dark-border' : 'border-light-border text-light-text hover:bg-light-bg'
          } flex items-center justify-center gap-2`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-edit-line" />
            </div>
            Düzenle
          </button>
          <button
            onClick={() => setActiveTab('pdf')}
            className={`py-2.5 px-3 font-medium text-sm rounded-lg border transition-colors cursor-pointer ${
              isDark ? 'border-dark-border text-dark-text hover:bg-dark-border' : 'border-light-border text-light-text hover:bg-light-bg'
            } flex items-center justify-center gap-1`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-pdf-line" />
            </div>
            PDF
          </button>
        </div>
      </div>
    </div>
  );
}
